import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Listing } from 'models/Listing';
import { useTranslation } from 'react-i18next';
import utils, { checkIsUrgent, getAcceptedListingContacts } from 'utils';
import ResolveImage from 'components/ResolveImage';
import { ImageSlider } from 'ncoded-component-library';
import { Link, useNavigate } from 'react-router-dom';
import PlatinumIcon from 'icons/Platinum.icon';
import ListingPrices from 'components/ListingPrices';
import { getImgUrl } from 'components/ResolveImage/utils';
import LocationmarkerIcon from 'icons/Locationmarker.icon';
import EoiDate from 'components/EoiDate';
import ListingContactsCardList from 'components/ListingContactsCardList';
import useLocationMapper from 'router/data/useLocationMapper';
import ListingActions from 'router/subrouters/Dash/subrouters/Business/components/ListingActions';

import './PremiumListingCard.styles.scss';
import './PremiumListingCard.styles.responsive.scss';

type PremiumListingCardProps = {
  className?: string;
  listing: Listing;
  onWatchlisted: (isWatchlisted: boolean) => void;
};

const PremiumListingCard: React.FC<PremiumListingCardProps> = (props) => {
  const { className, listing, onWatchlisted } = props;

  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    listingTitle,
    location,
    revenue,
    industry,
    gallery,
    slugName,
    averageStockValue,
    highlights,
    businessDescription,
    listingContact,
    EBITDA,
    askingPriceSelect,
    stockAtValue,
    type,
    locationPostcode,
    seekingExpressionOfInterestDate,
    currency,
    listingContacts,
    dealLocation,
    amount,
    amountFrom,
    amountTo,
    amountRequiredVisibleInfo,
    amountFromRequiredVisibleInfo,
    amountToRequiredVisibleInfo,
    revenueRequiredVisibleInfo,
    EBITDARequiredVisibleInfo,
    averageStockValueRequiredVisibleInfo,
  } = listing;

  const classes = classNames('premium-listing-card', className);

  const isUrgentListing = checkIsUrgent(type);

  const firmLogo = listingContact?.firmLogo?.url;

  const { locationLabelMapper } = useLocationMapper();

  const redirectionListingLink = utils.composeBusinessUrl(
    industry,
    slugName,
    type,
  );

  const acceptedListingContacts = useMemo(
    () => getAcceptedListingContacts(listingContacts),
    [listingContacts],
  );

  return (
    <div className={classes}>
      <span className="premium-listing-card__tier-badge">
        <PlatinumIcon />
        {t('platinum')}
      </span>
      <div className="premium-listing-card__content">
        <div className="premium-listing-card__content__main-content">
          <Link
            className="premium-listing-card__content__main-content__title"
            to={redirectionListingLink}
          >
            {listingTitle}
          </Link>
          <Link
            to={redirectionListingLink}
            className="premium-listing-card__content__main-content__listing-type"
          >
            <span>
              {isUrgentListing ? (
                <>
                  <LocationmarkerIcon />
                  {locationPostcode}
                </>
              ) : (
                locationLabelMapper[location?.state] || dealLocation
              )}
            </span>
            <span>{industry}</span>
          </Link>
          <EoiDate
            seekingExpressionOfInterestDate={seekingExpressionOfInterestDate}
          />
          <ListingPrices
            askingPrice={askingPriceSelect}
            amount={amount}
            amountFrom={amountFrom}
            amountTo={amountTo}
            revenue={revenue}
            EBITDA={EBITDA}
            averageStockValue={averageStockValue}
            stockAtValue={stockAtValue}
            currency={currency}
            amountRequiredVisibleInfo={amountRequiredVisibleInfo}
            amountFromRequiredVisibleInfo={amountFromRequiredVisibleInfo}
            amountToRequiredVisibleInfo={amountToRequiredVisibleInfo}
            revenueRequiredVisibleInfo={revenueRequiredVisibleInfo}
            EBITDARequiredVisibleInfo={EBITDARequiredVisibleInfo}
            averageStockValueRequiredVisibleInfo={
              averageStockValueRequiredVisibleInfo
            }
          />
          <Link
            to={redirectionListingLink}
            className="premium-listing-card__content__main-content__description"
          >
            {businessDescription}
          </Link>
          {!!highlights.length && (
            <ul className="premium-listing-card__content__main-content__highlights">
              {highlights.slice(0, 2).map((highlight, i) => (
                <li key={i}>
                  <div>
                    <Link to={redirectionListingLink}>{highlight}</Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="premium-listing-card__content__image-container">
          {gallery?.length > 1 ? (
            <ImageSlider
              onImagePress={() => {
                navigate(redirectionListingLink);
              }}
              slides={gallery.map(({ url }) => ({
                imageUrl: getImgUrl(url, '480'),
              }))}
              showDots={!!gallery?.length}
              className="premium-listing-card__image-container__slider"
            />
          ) : (
            <ResolveImage
              className="premium-listing-card__image-container__image"
              src={getImgUrl(gallery?.[0]?.url, '480')}
              shouldLazyLoad={false}
            />
          )}
        </div>

        {acceptedListingContacts?.[0]?.name || firmLogo ? (
          <div className="premium-listing-card__content__agent-details">
            <ListingContactsCardList
              listingContacts={acceptedListingContacts}
            />

            {firmLogo && (
              <ResolveImage
                asCompanyLogo
                className="premium-listing-card__content__agent-details__image"
                src={getImgUrl(firmLogo, '320')}
                shouldLazyLoad={false}
              />
            )}
          </div>
        ) : (
          <div />
        )}

        <ListingActions listing={listing} onWatchlisted={onWatchlisted} />
      </div>
    </div>
  );
};

export default PremiumListingCard;
